<template>
  <div class="card-history">
    <div class="card-header">
      <div class="title">{{$t('capture.latestClips.title')}}</div>
      <div class="sub-title">Updated {{lastUpdated}}
        <span
          class="text-light ml-1 link"
          @click.stop="load">
            <i class="fas fa-sync-alt"></i>
          </span>
      </div>
    </div>
    <!-- Loading State -->
    <div class="card-body" v-if="isLoading">
      <div class="list-group list-group-history list-group-history-error">
        <div class="list-group-item">
          <div class="history-message">
            <div class="title">Loading...</div>
          </div>
        </div>
      </div>
    </div>
    <!-- Content -->
    <div class="card-body" v-if="!isLoading">
      <div
        class="list-group list-group-history list-group-history-error"
        v-if="error && !hasTrimmedVideo">
        <div class="list-group-item">
          <div class="history-message">
            <div class="title">Sorry, Something wrong with latest clips</div>
            <div class="sub-title">Please try again later</div>
          </div>
        </div>
      </div>
      <div
        class="list-group list-group-history list-group-history-error"
        v-if="!error && !hasTrimmedVideo">
        <div class="list-group-item">
          <div class="history-message">
            <div class="title">{{$t('browser.empty.title')}}</div>
          </div>
        </div>
      </div>
      <ul
        class="list-group list-group-history"
        v-if="hasTrimmedVideo">
        <li
          :class="{'new-item': clip.newItem }"
          class="list-group-item list-group-item-action"
          v-for="(clip, index) in clips"
          :key="index">
          <div class="history-info">
            <div class="title">{{ clip.title || getChannelNameById(clip.channelId) }}</div>
            <small class="long-date">{{prettyFormatPeriod(clip.startedAt, clip.endedAt)}}</small>
            <small class="short-date">{{prettyFormatPeriodShort(clip.startedAt, clip.endedAt)}}</small>
            <div class="detail">
              <span class="detail-item">{{ parseDuration(clip.duration) }}</span>
            </div>
          </div>
          <div class="history-progress">
            <span class="btn-processing" v-if="!clip.availabledAt">
              Processing...
            </span>
            <a
              :href="getFileURL(clip)"
              class="btn btn-link btn-download"
              v-if="clip.availabledAt"
              target="_blank">
              <span>{{$t('common:download')}}</span>
            </a>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { loadClips } from '@/api/TimeMachine';
import {
  prettyFormatPeriod,
  parseDuration,
  parseToTime,
  prettyFormatPeriodShort,
} from '@/utils';

export default {
  props: {
    channel: {
      type: Object,
    },
    limit: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      state: {
        isLoading: true,
      },
      tempClips: [],
      clips: [],
      updateInterval: null,
      autoRefresh: 10000, // 10 sec
      lastUpdated: null,
      error: null,
    };
  },
  created() {
    this.load();
    this.updateInterval = setInterval(() => {
      this.load();
    }, this.autoRefresh);
  },
  computed: {
    baseURL() {
      return process.env.VUE_APP_ORIGIN || window.location.origin;
    },
    hasTrimmedVideo() {
      return this.clips.length > 0;
    },
    isLoading() {
      return this.state.isLoading;
    },
  },
  methods: {
    prettyFormatPeriod,
    parseDuration,
    parseToTime,
    prettyFormatPeriodShort,
    getFileURL(clip) {
      // eslint-disable-next-line no-underscore-dangle
      return `${this.baseURL}/api/clips/${clip._id}/files/video.mp4`;
    },
    getChannelNameById(channelId) {
      const channel = _.find(this.channels, item => item._id === channelId);
      return channel ? channel.name : 'Unknown Channel';
    },
    async load() {
      try {
        this.error = null;
        this.tempClips = _.cloneDeep(this.clips);

        const clips = await loadClips({ limit: this.limit });
        this.clips = this.appendStatusToClip(clips.data.data);
        this.lastUpdated = parseToTime(new Date());
        this.state.isLoading = false;
      } catch (error) {
        this.error = error;
      }
    },
    newItems(newClips, oldClips) {
      if (this.tempClips.length === 0) {
        return newClips;
      }
      return _.filter(newClips, clip => _.find(oldClips, oldItem => oldItem === clip));
    },
    isExistingItem(tempClips, clip) {
      if (this.tempClips.length === 0) {
        return true;
      }
      return _.find(this.tempClips, temp => temp._id === clip._id);
    },
    appendStatusToClip(clips) {
      return _.map(clips, clip => ({
        ...clip,
        isDownloading: false,
        newItem: !this.isExistingItem(this.tempClips, clip),
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/themes/transition.scss';
@import '~@/assets/scss/variables.scss';

.link {
  cursor: pointer;
  &:hover {
    color: $primary;
  }
}

.short-date {
  display: none;
}

.card-history {
  min-height: 100%;
  max-height: 100%;
  overflow-y: auto;
  color: $gray-400;

  .card-header {
    padding: 0;
    padding-bottom: $spacer * 0.5;
    font-size: $font-size-sm * 0.85;

    display: flex;
    justify-content: space-between;

    .sub-title {
      color: $gray-600;
      font-size: $font-size-sm * 0.75;
    }
  }

  .card-body {
    padding: 0;
  }

  .card-footer {
    padding: $spacer * 0.25 0;
  }
}

.list-group-history {
  overflow: hidden;

  .list-group-item {
    // @extend .fade;
    background: none;
    padding: $spacer * 0.5 $spacer * 0.75;
    font-size: $font-size-sm;
    color: $gray-600;
    border-color: $dark-500;
    display: flex;
    justify-content: space-between;

    .title {
      font-weight: bold;
      color: $gray-500;
      margin-right: 4px;
    }

    .detail {
      font-size: $font-size-sm * 0.75;
      margin-top: $spacer * 0.25;
      .detail-item {
        padding: 2px 8px;
        margin-right: 4px;
        background-color: $dark-500;
        color: #FFF;
        border-radius: 3px;
      }
    }

    .history-progress {
      align-self: center;
      font-size: $font-size-sm * 0.75;
      .btn-download {
        font-size: $font-size-sm * 0.75;
        padding-left: 0;
        padding-right: 0;
        color: #FFF;
      }
    }
  }
}

.list-group-history-error {

  .list-group-item {
    width: 100%;
    height: 80px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .sub-title {
      font-size: $font-size-sm;
    }
  }
}

.list-group-item.new-item {
  transition: all 0.2s ease-in;
  animation: new-trimmed-clip 0.5s ease-in;
}

@media screen and (max-width: 1366px) {
  .list-group-history {
    .list-group-item:nth-child(4):nth-last-child(1) {
      display: none;
    }
  }
}
@media screen and (max-width: 1020px) {
  .long-date {
    display: none;
  }
  .short-date {
    display: block;
  }
  .list-group-history {
    .list-group-item:nth-child(3) {
      display: none;
    }
  }
}
</style>
